/**
 * Page de connexion du portail étudiant.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { LogIn, KeyRound, Search, Loader2, Fingerprint } from 'lucide-react';
import { PortailLayout } from '../PortailLayout';
import { portailApi } from '../services/portailApi';
import {
  isWebAuthnSupported, decodeRequestOptions, encodeAssertionCredential,
} from '../services/webauthn';

export default function PortailLogin() {
  const navigate = useNavigate();
  const [matricule, setMatricule] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [biometricSupporte] = useState<boolean>(() => isWebAuthnSupported());
  const [aBiometrique, setABiometrique] = useState<boolean>(false);
  const [biometricLoading, setBiometricLoading] = useState(false);

  // Quand le matricule change : vérifier si l'étudiant a des credentials WebAuthn
  useEffect(() => {
    const mat = matricule.trim().toUpperCase();
    if (!biometricSupporte || mat.length < 6) {
      setABiometrique(false);
      return;
    }
    const t = setTimeout(() => {
      portailApi.webauthnHasCredentials(mat)
        .then((r) => setABiometrique(r.has_credentials))
        .catch(() => setABiometrique(false));
    }, 400);
    return () => clearTimeout(t);
  }, [matricule, biometricSupporte]);

  const loginBiometrique = async () => {
    const mat = matricule.trim().toUpperCase();
    if (!mat) {
      setError('Saisissez votre matricule');
      return;
    }
    setError(null);
    setBiometricLoading(true);
    try {
      const opts = await portailApi.webauthnLoginBegin(mat);
      const cred = await navigator.credentials.get({
        publicKey: decodeRequestOptions(opts),
      }) as PublicKeyCredential;
      if (!cred) throw new Error('Authentification annulée');
      const enc = encodeAssertionCredential(cred);
      const res = await portailApi.webauthnLoginComplete(mat, enc);
      portailApi.setToken(res.access_token);
      portailApi.setEtudiant({
        matricule: res.matricule, nom: res.nom, prenoms: res.prenoms,
      });
      navigate('/portail/accueil', { replace: true });
    } catch (e: any) {
      const msg = e?.name === 'NotAllowedError'
        ? "Empreinte refusée ou annulée."
        : (e?.data?.detail || e?.message || 'Erreur biométrique');
      setError(msg);
    } finally {
      setBiometricLoading(false);
    }
  };

  // Vérifie si le portail est en maintenance avant d'afficher le formulaire
  useEffect(() => {
    portailApi.getMaintenanceStatus().then((res) => {
      if (res.actif) {
        sessionStorage.setItem('portail_maintenance', JSON.stringify({
          message: res.message,
          depuis: res.depuis,
        }));
        navigate('/portail/maintenance', { replace: true });
      }
    }).catch(() => { /* ignore */ });
  }, [navigate]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const mat = matricule.trim().toUpperCase();
      const res = await portailApi.login({ matricule: mat, password });
      portailApi.setToken(res.access_token);
      portailApi.setEtudiant({
        matricule: res.matricule,
        nom: res.nom,
        prenoms: res.prenoms,
      });
      // Demande au navigateur d'enregistrer les identifiants (iCloud Keychain,
      // Google Password Manager, gestionnaire Firefox…). L'utilisateur voit
      // la prompt native "Enregistrer ce mot de passe ?" — bien plus sûr que
      // stocker le mot de passe en clair côté client.
      try {
        const NavAny = window as any;
        if (NavAny.PasswordCredential && (navigator as any).credentials?.store) {
          const cred = new NavAny.PasswordCredential({
            id: mat,
            password,
            name: `${res.prenoms || ''} ${res.nom || ''}`.trim() || mat,
          });
          await (navigator as any).credentials.store(cred);
        }
      } catch { /* Safari iOS ne supporte pas encore, fallback autofill du form */ }
      navigate('/portail/accueil', { replace: true });
    } catch (e: any) {
      if (e.status === 404) {
        setError("Compte non créé. Veuillez d'abord créer votre mot de passe.");
      } else {
        setError(e.message || 'Erreur de connexion');
      }
    } finally {
      setLoading(false);
    }
  };

  // Pré-remplissage via Credential Management API (Chrome/Edge/Firefox)
  // — au chargement de la page, si le navigateur a enregistré des identifiants
  // pour ce site, on tente une pré-authentification silencieuse.
  useEffect(() => {
    try {
      const NavAny = window as any;
      if (!NavAny.PasswordCredential || !(navigator as any).credentials?.get) return;
      (navigator as any).credentials.get({
        password: true,
        mediation: 'silent',
      }).then((cred: any) => {
        if (cred && cred.type === 'password' && cred.id && cred.password) {
          setMatricule((cred.id || '').toUpperCase());
          setPassword(cred.password);
        }
      }).catch(() => { /* silencieux */ });
    } catch { /* */ }
  }, []);

  return (
    <PortailLayout>
      <div className="min-h-[80vh] flex flex-col justify-center">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="2i" className="w-20 h-20 mx-auto rounded-md shadow-md ring-2 ring-amber-500/30 bg-white p-1" />
          <h1 className="text-2xl font-bold text-gray-100 mt-4">
            Institut International 2i
          </h1>
          <p className="text-sm text-gray-400 mt-2">
            Portail des résultats étudiants
          </p>
        </div>

        <form
          onSubmit={submit}
          className="bg-gray-900 border border-gray-800 rounded-xl shadow-xl p-6 space-y-4"
          method="post"
          action="#login"
          autoComplete="on"
        >
          <div>
            <label htmlFor="portail-matricule" className="block text-sm font-medium text-gray-300 mb-1">
              Matricule
            </label>
            <input
              id="portail-matricule"
              type="text"
              name="username"
              autoComplete="username"
              inputMode="text"
              value={matricule}
              onChange={(e) => setMatricule(e.target.value)}
              placeholder="Ex: 2IP251234"
              autoCapitalize="characters"
              required
              className="w-full px-4 py-3 border border-gray-700 rounded-lg bg-gray-800 text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label htmlFor="portail-password" className="block text-sm font-medium text-gray-300 mb-1">
              Mot de passe
            </label>
            <input
              id="portail-password"
              type="password"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-4 py-3 border border-gray-700 rounded-lg bg-gray-800 text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {error && (
            <div className="p-3 bg-red-900/30 border border-red-800 rounded-lg text-sm text-red-300">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-semibold py-3 rounded-lg disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <LogIn size={18} />}
            {loading ? 'Connexion...' : 'Se connecter'}
          </button>

          {biometricSupporte && aBiometrique && (
            <button
              type="button"
              onClick={loginBiometrique}
              disabled={biometricLoading}
              className="w-full bg-green-600 hover:bg-green-700 active:bg-green-800 text-white font-semibold py-3 rounded-lg disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2"
            >
              {biometricLoading ? <Loader2 size={18} className="animate-spin" /> : <Fingerprint size={18} />}
              {biometricLoading ? 'Vérification…' : 'Se connecter avec empreinte'}
            </button>
          )}

          <div className="space-y-2 pt-2 border-t border-gray-800">
            <Link
              to="/portail/premiere-connexion"
              className="flex items-center justify-center gap-2 text-sm text-blue-400 hover:text-blue-300 hover:underline py-1"
            >
              <KeyRound size={14} />
              Première connexion ? Créer mon mot de passe
            </Link>
            <Link
              to="/portail/mot-de-passe-oublie"
              className="block text-center text-sm text-gray-400 hover:text-gray-200 py-1"
            >
              Mot de passe oublié ?
            </Link>
            <Link
              to="/portail/chercher-matricule"
              className="flex items-center justify-center gap-2 text-sm text-gray-400 hover:text-gray-200 py-1"
            >
              <Search size={14} />
              Je ne connais pas mon matricule
            </Link>
          </div>
        </form>

        <p className="text-center text-xs text-gray-400 mt-6">
          Année académique 2025-2026
        </p>
      </div>
    </PortailLayout>
  );
}
